import Keycloak from 'keycloak-js'
import type { User, UserRole } from '@/types'

// Config from window or defaults
const config = {
  url: window.CONFIG?.KEYCLOAK_URL || 'https://diab.pratiscare.com/auth',
  realm: window.CONFIG?.KEYCLOAK_REALM || 'diabassist',
  clientId: window.CONFIG?.KEYCLOAK_CLIENT_ID || 'diabassist-web',
}

// Demo mode - skip real Keycloak auth
const DEMO_MODE = window.CONFIG?.DEMO_MODE === true

// Keycloak instance - use window to survive HMR/reload
declare global {
  interface Window {
    __keycloak__?: Keycloak
    __keycloakInitPromise__?: Promise<boolean>
    __keycloakInitialized__?: boolean
    __keycloakDemoUser__?: User
    __keycloakDemoAuth__?: boolean
  }
}

// Use window-level singletons to prevent re-initialization
let keycloak: Keycloak | null = window.__keycloak__ || null
let initPromise: Promise<boolean> | null = window.__keycloakInitPromise__ || null
let isInitialized = window.__keycloakInitialized__ || false
let demoUser: User | null = window.__keycloakDemoUser__ || null
let demoAuthenticated = window.__keycloakDemoAuth__ || false

/**
 * Initialize Keycloak - SINGLETON pattern with window-level state
 * Must be called BEFORE Vue Router is created
 */
export async function initKeycloak(): Promise<boolean> {
  // Strict singleton check
  if (initPromise) {
    console.log('[Keycloak] Already initializing, returning existing promise')
    return initPromise
  }
  if (isInitialized) {
    console.log('[Keycloak] Already initialized, returning cached state:', keycloak?.authenticated ?? demoAuthenticated)
    return keycloak?.authenticated ?? demoAuthenticated
  }

  console.log('[Keycloak] Starting fresh initialization')
  initPromise = doInitKeycloak()
  window.__keycloakInitPromise__ = initPromise
  return initPromise
}

async function doInitKeycloak(): Promise<boolean> {
  // DEMO MODE - for local development without Keycloak
  if (DEMO_MODE) {
    console.log('[Keycloak] DEMO MODE enabled')
    demoUser = {
      id: 'demo-provider-001',
      email: 'dr.demo@diabassist.ma',
      name: 'Dr. Demo Provider',
      firstName: 'Demo',
      lastName: 'Provider',
      role: 'provider' as UserRole,
      roles: ['provider', 'platform_admin'] as UserRole[],
      locale: 'fr',
      createdAt: new Date().toISOString(),
    }
    demoAuthenticated = true
    isInitialized = true
    // Save to window
    window.__keycloakDemoUser__ = demoUser
    window.__keycloakDemoAuth__ = true
    window.__keycloakInitialized__ = true
    return true
  }

  // Real Keycloak authentication
  keycloak = new Keycloak(config)
  window.__keycloak__ = keycloak

  // Set up event handlers BEFORE init (required by keycloak-js)
  keycloak.onReady = (authenticated) => {
    console.log('[Keycloak] onReady, authenticated:', authenticated)
  }

  keycloak.onAuthSuccess = () => {
    console.log('[Keycloak] onAuthSuccess')
  }

  keycloak.onAuthError = (error) => {
    console.error('[Keycloak] onAuthError:', error)
  }

  keycloak.onTokenExpired = () => {
    console.log('[Keycloak] Token expired, refreshing...')
    keycloak?.updateToken(30).catch(() => {
      console.warn('[Keycloak] Token refresh failed, user may need to re-login')
    })
  }

  try {
    console.log('[Keycloak] Starting initialization...')
    console.log('[Keycloak] Config:', { url: config.url, realm: config.realm, clientId: config.clientId })
    console.log('[Keycloak] Current URL:', window.location.href)

    // Check if we're returning from Keycloak auth (has code in URL)
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const hasAuthCode = urlParams.has('code') || hashParams.has('code')
    const hasAuthState = urlParams.has('state') || hashParams.has('state')

    console.log('[Keycloak] Has auth code:', hasAuthCode, 'Has state:', hasAuthState)

    // IMPORTANT: Disable silent SSO - it causes iframe issues
    // Use standard check-sso which works more reliably
    const authenticated = await keycloak.init({
      onLoad: 'check-sso',
      checkLoginIframe: false, // Disable - causes issues with modern browsers
      pkceMethod: 'S256',
      enableLogging: true,
    })

    console.log('[Keycloak] Init complete, authenticated:', authenticated)
    isInitialized = true
    window.__keycloakInitialized__ = true

    if (authenticated) {
      setupTokenRefresh()
      // Clean URL AFTER Keycloak has processed the callback
      setTimeout(() => cleanupUrl(), 100)
    }

    return authenticated
  } catch (error) {
    console.error('[Keycloak] Init failed:', error)
    isInitialized = true
    window.__keycloakInitialized__ = true
    return false
  }
}

/**
 * Clean OAuth parameters from URL after successful authentication
 * This prevents Vue Router from seeing the long callback URL
 */
function cleanupUrl() {
  try {
    const url = new URL(window.location.href)
    const hash = window.location.hash

    // Check for OAuth params in query string
    const hasQueryParams = url.searchParams.has('code') ||
                           url.searchParams.has('state') ||
                           url.searchParams.has('session_state')

    // Check for OAuth params in hash fragment
    const hasHashParams = hash.includes('code=') ||
                          hash.includes('state=') ||
                          hash.includes('session_state=') ||
                          hash.includes('error=')

    if (hasQueryParams || hasHashParams) {
      // Get clean path (preserve any app route if present)
      const cleanUrl = url.pathname || '/'
      window.history.replaceState(null, '', cleanUrl)
      console.log('[Keycloak] URL cleaned to:', cleanUrl)
    }
  } catch (e) {
    console.warn('[Keycloak] URL cleanup failed:', e)
  }
}

/**
 * Login via Keycloak
 */
export async function login(): Promise<void> {
  if (DEMO_MODE) {
    demoAuthenticated = true
    return
  }

  if (!keycloak) {
    throw new Error('Keycloak not initialized')
  }

  const redirectUri = window.location.origin + '/'
  console.log('[Keycloak] Redirecting to login, redirect URI:', redirectUri)

  await keycloak.login({
    redirectUri: redirectUri,
  })
}

/**
 * Logout via Keycloak
 */
export function logout(): void {
  if (DEMO_MODE) {
    demoAuthenticated = false
    demoUser = null
    window.location.href = '/'
    return
  }

  if (!keycloak) return

  const redirectUri = window.location.origin + '/'
  const logoutUrl = keycloak.createLogoutUrl({ redirectUri })
  window.location.href = logoutUrl
}

/**
 * Get current access token
 */
export function getToken(): string | undefined {
  if (DEMO_MODE) {
    return 'demo-token-for-local-development'
  }
  return keycloak?.token
}

/**
 * Check if authenticated
 */
export function isAuthenticated(): boolean {
  if (DEMO_MODE) {
    return demoAuthenticated
  }
  return keycloak?.authenticated ?? false
}

/**
 * Refresh token - only if already authenticated
 * IMPORTANT: Don't attempt refresh if not authenticated, it will trigger prompt=none auth check
 */
export async function refreshToken(minValidity = 30): Promise<boolean> {
  if (DEMO_MODE) return true
  if (!keycloak) return false

  // CRITICAL: Only refresh if already authenticated
  // Otherwise keycloak.updateToken() will trigger a silent auth check (prompt=none)
  // which causes redirect loops
  if (!keycloak.authenticated) {
    console.warn('[Keycloak] Cannot refresh token - not authenticated')
    return false
  }

  try {
    const refreshed = await keycloak.updateToken(minValidity)
    if (refreshed) {
      console.log('[Keycloak] Token refreshed')
    }
    return true
  } catch (error) {
    console.error('[Keycloak] Token refresh failed:', error)
    return false
  }
}

function setupTokenRefresh(): void {
  setInterval(async () => {
    if (keycloak?.authenticated) {
      await refreshToken()
    }
  }, 60000)
}

/**
 * Extract user from token
 */
export function getUserFromToken(): User | null {
  if (DEMO_MODE) return demoUser
  if (!keycloak?.tokenParsed) return null

  const token = keycloak.tokenParsed as {
    sub: string
    email?: string
    name?: string
    given_name?: string
    family_name?: string
    preferred_username?: string
    realm_access?: { roles: string[] }
    roles?: string[]
    locale?: string
  }

  const allRoles = [
    ...(token.realm_access?.roles || []),
    ...(token.roles || []),
  ]

  const appRoles: UserRole[] = [
    'platform_admin', 'system_admin', 'provider_admin', 'clinic_admin',
    'provider', 'clinician', 'coach', 'aide_soignant', 'caregiver', 'patient',
  ]

  const userRoles = appRoles.filter(role => allRoles.includes(role))
  const primaryRole = userRoles[0] || 'patient'

  return {
    id: token.sub,
    email: token.email || token.preferred_username || '',
    name: token.name || token.preferred_username || 'Utilisateur',
    firstName: token.given_name,
    lastName: token.family_name,
    role: primaryRole,
    roles: userRoles,
    locale: (token.locale as 'fr' | 'ar' | 'darija') || 'fr',
    createdAt: new Date().toISOString(),
  }
}

export function getKeycloak(): Keycloak | null {
  return keycloak
}

export function onTokenUpdate(callback: (token: string) => void): void {
  if (keycloak) {
    keycloak.onAuthRefreshSuccess = () => {
      if (keycloak?.token) {
        callback(keycloak.token)
      }
    }
  }
}

export function getRefreshToken(): string | undefined {
  return keycloak?.refreshToken
}
